"use client";

import React, { useEffect, useState } from "react";
import {
  Globe,
  ShieldCheck,
  TrendingUp,
  BarChart3,
  Search,
  AlertTriangle,
  Building2,
  Landmark
} from "lucide-react";

interface LeaderboardItem {
  domain: string;
  total_strikes: number;
  hostname_count?: number;
  success_count: number;
  total_count: number;
  success_rate: number;
  /* What the rate does NOT cover (option 2, owner 2026-09-10). The row stays
     concluded-only — a case still being worked is not a failure — but a bare
     100% beside a domain under active enforcement was the misleading part. */
  cases_concluded?: number;
  cases_resolved?: number;
  cases_in_flight?: number;
}

interface EntityStat {
  entity: string;
  notices_sent: number;
  cases: number;
  cases_resolved: number;
  resolution_rate: number;
  median_days_to_resolution: number | null;
}

interface TransparencyData {
  total_domains_targeted: number;
  total_links_neutralized: number;
  link_success_rate: number;
  cases_in_flight?: number;
  leaderboard: LeaderboardItem[];
  hosting_provider_stats?: EntityStat[];
  registrar_stats?: EntityStat[];
  unattributed_notices?: number;
  resolution_levels?: { owner_contact: number; hosting: number; registrar: number };
}

export default function TransparencyPage() {
  const [data, setData] = useState<TransparencyData | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await fetch("/api/transparency");
        if (!res.ok) {
          throw new Error("Failed to load compliance statistics.");
        }
        const json = await res.json();
        setData(json);
      } catch (err: any) {
        setError(err.message || "An unexpected error occurred.");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const filteredLeaderboard = data
    ? data.leaderboard.filter((item) =>
        item.domain.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  return (
    <div className="bg-canvas min-h-screen py-16 px-6 md:px-8 relative overflow-hidden">

      {/* Background glow blobs */}
      <div className="absolute top-0 left-1/4 h-[500px] w-[500px] rounded-full bg-blue-500/5 blur-[120px] pointer-events-none" />
      <div className="absolute top-40 right-1/4 h-[600px] w-[600px] rounded-full bg-indigo-600/5 blur-[150px] pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10 space-y-12">

        {/* Header block */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <h1 className="text-4xl md:text-5xl font-black text-primary tracking-tight">
            Transparency <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Report</span>
          </h1>
          <p className="text-sm text-secondary leading-relaxed">
            Real-time enforcement statistics. We track compliance across the global web to help you understand where your privacy is most at risk.
          </p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 space-y-4">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-primary border-t-transparent" />
            <span className="text-xs font-mono text-secondary uppercase tracking-widest animate-pulse">Compiling global stats...</span>
          </div>
        ) : error ? (
          <div className="rounded-2xl bg-status-danger-bg p-6 flex items-center justify-center text-sm text-status-danger-text max-w-xl mx-auto">
            <AlertTriangle className="h-4 w-4 mr-2" /> {error}
          </div>
        ) : (
          <>
            {/* Top stat cards row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

              {/* Card 1: Domains Targeted */}
              <div className="rounded-2xl bg-card p-6 flex items-center justify-between shadow-sm">
                <div className="space-y-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-secondary">Domains Targeted</span>
                  <div className="text-3xl font-black text-primary">{data?.total_domains_targeted}</div>
                </div>
                <div className="h-12 w-12 rounded-xl bg-status-info-bg flex items-center justify-center text-status-info-text">
                  <Globe className="h-5 w-5" />
                </div>
              </div>

              {/* Card 2: Links Neutralized */}
              <div className="rounded-2xl bg-card p-6 flex items-center justify-between shadow-sm">
                <div className="space-y-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-secondary">Links Neutralized</span>
                  <div className="text-3xl font-black text-primary">{data?.total_links_neutralized}</div>
                </div>
                <div className="h-12 w-12 rounded-xl bg-status-success-bg flex items-center justify-center text-status-success-text">
                  <ShieldCheck className="h-5 w-5" />
                </div>
              </div>

              {/* Card 3: Success Rate
                  LINK-LEVEL since 2026-09-10, and no longer an average of
                  per-domain averages — that weighted every operator equally
                  regardless of size, so one link at 100% counted as much as
                  forty at 10%. It published 93.5% where the true figure was
                  96.4%: wrong in our favour that day, and wrong in either
                  direction on any other, which is worse than a bias.

                  The figure counts CONCLUDED cases only — an open case has no
                  outcome yet, and counting it as a non-success understated the
                  record. Since that makes the number look better, the basis and
                  the excluded count are stated on the page rather than left for
                  a reader to wonder about. */}
              <div className="rounded-2xl bg-card p-6 flex items-center justify-between shadow-sm">
                <div className="space-y-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-secondary">Links Removed</span>
                  <div className="text-3xl font-black text-primary">{data?.link_success_rate}%</div>
                  <p className="text-2xs leading-snug text-muted max-w-[15rem]">
                    Of every link on a completed case
                    {typeof data?.cases_in_flight === "number" && data.cases_in_flight > 0
                      ? ` — ${data.cases_in_flight} more still in progress and not counted`
                      : ""}
                    .
                  </p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-indigo-500/10 flex items-center justify-center text-indigo-500">
                  <TrendingUp className="h-5 w-5" />
                </div>
              </div>

            </div>

            {/* Leaderboard table block */}
            <div className="rounded-2xl bg-card p-6 shadow-sm space-y-6">

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-divider pb-5">
                <div className="flex items-center gap-2.5">
                  <BarChart3 className="h-5 w-5 text-brand-primary" />
                  <h2 className="text-lg font-bold text-primary tracking-tight">Domain Enforcement Leaderboard</h2>
                </div>

                {/* Search input bar */}
                <div className="relative w-full sm:w-80">
                  <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-secondary">
                    <Search className="h-3.5 w-3.5" />
                  </span>
                  <input
                    type="text"
                    placeholder="Filter by domain..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full rounded-xl bg-surface-inset pl-9 pr-4 py-2.5 text-xs text-primary placeholder:text-muted focus:shadow-[0_0_0_3px_var(--focus-ring)] outline-none transition-all duration-300"
                  />
                </div>
              </div>

              {/* Table wrapper */}
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="text-[10px] font-bold uppercase tracking-widest text-secondary font-mono">
                      <th className="pb-3.5 pl-4">Rank</th>
                      <th className="pb-3.5">Domain</th>
                      <th className="pb-3.5 text-center">Total Strikes</th>
                      <th className="pb-3.5 text-center">Success</th>
                      <th className="pb-3.5 pr-4 text-right">Success Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeaderboard.length > 0 ? (
                      filteredLeaderboard.map((item, idx) => (
                        <tr
                          key={item.domain}
                          className="hover:bg-surface-inset text-xs font-semibold text-secondary transition-colors"
                        >
                          <td className="py-4 pl-4 font-mono text-[10px] text-muted">
                            #{idx + 1}
                          </td>
                          <td className="py-4 font-mono text-primary">
                            {item.domain}
                            {/* Enforcement against sub.example.com is enforcement
                                against example.com's operator, so figures are
                                aggregated. Stating the hostname count keeps the
                                report factual about what it counted. */}
                            {(item.hostname_count ?? 1) > 1 && (
                              <span
                                className="ml-2 font-sans text-[10px] font-medium text-muted"
                                title="Figures cover every hostname of this domain, including subdomains"
                              >
                                {item.hostname_count} hostnames
                              </span>
                            )}
                            {/* THE DISCLOSURE THAT FIXES THE MISREADING. A bare
                                100% next to seven strikes read as a finished
                                record; koselarab.com published 1/1 while another
                                case was still open after three DELIVERED
                                level-3 notices. The rate stays concluded-only —
                                an open case is not a failure — and the row now
                                says what the rate leaves out. */}
                            {(item.cases_in_flight ?? 0) > 0 && (
                              <span
                                className="ml-2 font-sans text-[10px] font-medium text-status-warning-text"
                                title="Still being worked, and deliberately not counted in the rate — an open case has no outcome yet. The rate covers this domain's completed cases only."
                              >
                                {item.cases_in_flight} still in progress
                              </span>
                            )}
                          </td>
                          <td className="py-4 text-center font-mono text-secondary">
                            {item.total_strikes}
                          </td>
                          <td className="py-4 text-center font-mono text-status-success-text">
                            {item.success_count} / {item.total_count}
                          </td>
                          <td className="py-4 pr-4">
                            <div className="flex items-center justify-end gap-3">
                              <span className="font-mono text-brand-primary text-2xs w-8 text-right">
                                {item.success_rate}%
                              </span>
                              <div className="w-20 bg-surface-inset rounded-full h-1 overflow-hidden">
                                <div
                                  className="bg-brand-primary h-full rounded-full transition-all duration-500"
                                  style={{ width: `${item.success_rate}%` }}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="py-12 text-center text-xs text-secondary font-mono">
                          No matching records found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="pt-4 border-t border-divider text-[10px] text-secondary font-mono leading-relaxed">
                * Enforcement statistics are dynamically computed from active data erasure strike operations. Success is defined as a host or domain registrar responding to a legal strike notice by removing public access to the infringing content.
              </div>

            </div>

            {/* Resolution-level breakdown */}
            {data?.resolution_levels && (
              <div className="rounded-2xl bg-card p-6 shadow-sm space-y-5">
                <h2 className="text-lg font-bold text-primary tracking-tight">Where Content Gets Removed</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { key: "owner_contact", label: "At Site Owner Contact" },
                    { key: "hosting", label: "At Hosting Provider" },
                    { key: "registrar", label: "At Domain Registrar" },
                  ].map((r) => (
                    <div key={r.key} className="rounded-xl bg-surface-inset p-5 text-center">
                      <div className="text-3xl font-black text-primary">
                        {data.resolution_levels![r.key as keyof typeof data.resolution_levels]}
                      </div>
                      <div className="mt-1 text-[10px] font-bold uppercase tracking-widest text-secondary">{r.label}</div>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-secondary font-mono">Counts of cases whose content was removed at each escalation stage.</p>
              </div>
            )}

            {/* Per-entity stats (factual numbers only — no compliance labels).
                The whole provider block only appears once at least one notice
                is attributed to a host/registrar — no empty tables on the
                public page; it self-populates as attribution accrues. */}
            {((data?.hosting_provider_stats?.length ?? 0) > 0 || (data?.registrar_stats?.length ?? 0) > 0) && (
              <>
            {[
              { title: "Hosting Providers", icon: Building2, rows: data?.hosting_provider_stats ?? [] },
              { title: "Domain Registrars", icon: Landmark, rows: data?.registrar_stats ?? [] },
            ].map((section) => (
              <div key={section.title} className="rounded-2xl bg-card p-6 shadow-sm space-y-6">
                <div className="flex items-center gap-2.5 border-b border-divider pb-5">
                  <section.icon className="h-5 w-5 text-brand-primary" />
                  <h2 className="text-lg font-bold text-primary tracking-tight">{section.title}</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="text-[10px] font-bold uppercase tracking-widest text-secondary font-mono">
                        <th className="pb-3.5 pl-4">Provider</th>
                        <th className="pb-3.5 text-center">Notices Sent</th>
                        <th className="pb-3.5 text-center">Cases</th>
                        <th className="pb-3.5 text-center">Resolved</th>
                        <th className="pb-3.5 text-center">Resolution Rate</th>
                        <th className="pb-3.5 pr-4 text-right">Median Days</th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.rows.length > 0 ? (
                        section.rows.map((e) => (
                          <tr key={e.entity} className="hover:bg-surface-inset text-xs font-semibold text-secondary transition-colors">
                            <td className="py-4 pl-4 font-mono text-primary">{e.entity}</td>
                            <td className="py-4 text-center font-mono">{e.notices_sent}</td>
                            <td className="py-4 text-center font-mono">{e.cases}</td>
                            <td className="py-4 text-center font-mono text-status-success-text">{e.cases_resolved}</td>
                            <td className="py-4 text-center font-mono text-brand-primary">{e.resolution_rate}%</td>
                            <td className="py-4 pr-4 text-right font-mono">{e.median_days_to_resolution ?? "—"}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="py-8 text-center text-xs text-secondary font-mono">
                            No per-provider data yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

            {/* Unattributed bucket — historical notices not itemized per provider. */}
            {(data?.unattributed_notices ?? 0) > 0 && (
              <p className="text-[11px] text-secondary leading-relaxed text-center">
                Provider breakdowns itemize notices attributed to a specific host or registrar. {data!.unattributed_notices} earlier
                {" "}notice{data!.unattributed_notices === 1 ? " is" : "s are"} included in the totals above but not broken out by provider.
              </p>
            )}
              </>
            )}
          </>
        )}

      </div>
    </div>
  );
}
